import { Show, createSignal, onMount, type JSX } from "solid-js";
import { ApiError, createAdminApi, type AdminApi } from "./api/client";
import { errorMessage } from "./api/errors";
import { Dashboard } from "./Dashboard";
import type { GatewayStatus } from "./model";

type SessionState =
  | { kind: "checking" }
  | { kind: "signed-out"; message: string | null }
  | { kind: "authenticated"; status: GatewayStatus }
  | { kind: "unavailable"; error: unknown };

export function App(): JSX.Element {
  const [session, setSession] = createSignal<SessionState>({ kind: "checking" });
  const api: AdminApi = createAdminApi({
    onUnauthorized: () => {
      setSession({ kind: "signed-out", message: "Operator Session 已失效，请重新登录。" });
    },
  });

  async function probe(): Promise<void> {
    setSession({ kind: "checking" });
    try {
      const result = await api.session.probe();
      if (result.authenticated) setSession({ kind: "authenticated", status: result.status });
      else setSession({ kind: "signed-out", message: null });
    } catch (error) {
      setSession({ kind: "unavailable", error });
    }
  }

  async function logout(): Promise<void> {
    let message: string | null = null;
    try {
      await api.session.logout();
    } catch (error) {
      message = error instanceof ApiError && error.mutationOutcomeUnknown
        ? "登出结果未知；本页面的敏感内存状态已清除。刷新前请确认 Gateway 可达。"
        : errorMessage(error);
    }
    setSession({ kind: "signed-out", message });
  }

  onMount(() => void probe());

  return (
    <>
      <Show when={session().kind === "checking"}>
        <CenteredState title="正在检查 Operator Session" message="通过 Gateway Status 探测现有 HttpOnly Cookie。" busy />
      </Show>
      <Show when={session().kind === "unavailable"}>
        <CenteredState
          title="Gateway 暂不可用"
          message={errorMessage((session() as Extract<SessionState, { kind: "unavailable" }>).error)}
          actionLabel="重新检查"
          onAction={() => void probe()}
        />
      </Show>
      <Show when={session().kind === "signed-out"}>
        <Login
          api={api}
          message={(session() as Extract<SessionState, { kind: "signed-out" }>).message}
          onAuthenticated={(status) => setSession({ kind: "authenticated", status })}
        />
      </Show>
      <Show when={session().kind === "authenticated"}>
        <Dashboard
          api={api}
          initialStatus={(session() as Extract<SessionState, { kind: "authenticated" }>).status}
          onLogout={logout}
        />
      </Show>
    </>
  );
}

function Login(props: {
  api: AdminApi;
  message: string | null;
  onAuthenticated: (status: GatewayStatus) => void;
}): JSX.Element {
  const [secret, setSecret] = createSignal("");
  const [error, setError] = createSignal<string | null>(props.message);
  const [busy, setBusy] = createSignal(false);

  async function submit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (secret() === "" || busy()) return;
    const submittedSecret = secret();
    setSecret("");
    setBusy(true);
    setError(null);
    try {
      await props.api.session.login(submittedSecret);
      const result = await props.api.session.probe();
      if (!result.authenticated) throw new ApiError({ kind: "http", code: "unauthorized", status: 401 });
      props.onAuthenticated(result.status);
    } catch (caught) {
      if (caught instanceof ApiError && caught.mutationOutcomeUnknown) {
        try {
          const result = await props.api.session.probe();
          if (result.authenticated) {
            props.onAuthenticated(result.status);
            return;
          }
        } catch {
          // Preserve the original unknown-outcome message without exposing the follow-up response.
        }
      }
      setError(caught instanceof ApiError && caught.status === 401 ? "Operator Secret 不正确。" : errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main class="login-shell">
      <section class="login-card">
        <div class="brand brand--login"><span class="brand__mark">AR</span><span><strong>Agent Relay</strong><small>Control Surface</small></span></div>
        <div class="login-card__copy">
          <p class="eyebrow">Operator Access</p>
          <h1>进入管理控制面</h1>
          <p>Secret 只存在当前表单中，提交后立即清空。会话由 HttpOnly Cookie 维持。</p>
        </div>
        <form onSubmit={(event) => void submit(event)}>
          <label class="field">
            <span>Operator Secret</span>
            <input
              type="password"
              name="operator-secret"
              autocomplete="off"
              autofocus
              value={secret()}
              onInput={(event) => setSecret(event.currentTarget.value)}
            />
          </label>
          <button type="submit" class="button button--primary button--large" disabled={busy() || secret() === ""}>
            {busy() ? "正在验证…" : "登录"}
          </button>
        </form>
        <Show when={error()}>{(message) => <p class="callout callout--danger" role="alert">{message()}</p>}</Show>
        <small class="login-card__footnote">浏览器与 Gateway 必须从完全相同的 Origin 访问。</small>
      </section>
    </main>
  );
}

function CenteredState(props: {
  title: string;
  message: string;
  busy?: boolean;
  actionLabel?: string;
  onAction?: () => void;
}): JSX.Element {
  return (
    <main class="login-shell">
      <section class="login-card login-card--state">
        <div class="brand brand--login"><span class="brand__mark">AR</span><span><strong>Agent Relay</strong><small>Control Surface</small></span></div>
        <div class="login-card__copy"><p class="eyebrow">Gateway Admin</p><h1>{props.title}</h1><p>{props.message}</p></div>
        {props.busy ? <div class="loading-bar" aria-label="加载中"><span /></div> : null}
        {props.actionLabel && props.onAction ? <button type="button" class="button button--primary" onClick={props.onAction}>{props.actionLabel}</button> : null}
      </section>
    </main>
  );
}
